import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Alert,
    Snackbar,
    Grid,
    useTheme,
    alpha
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

// Type definitions
interface HostingPlan {
    id: number | null;
    planName: string;
    hostingRate: number;
    hydroSetupFee: number;
    airSetupFee: number;
    depositDays: number;
    description: string;
}

interface FormData {
    id: number | null;
    planName: string;
    hostingRate: string;
    hydroSetupFee: string;
    airSetupFee: string;
    depositDays: string;
    description: string;
}

interface SnackbarState {
    open: boolean;
    message: string;
    severity: 'success' | 'error';
}

export default function HostingSetupController() {
    const theme = useTheme();
    
    const [hostingPlans, setHostingPlans] = useState<HostingPlan[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [editingPlan, setEditingPlan] = useState<HostingPlan | null>(null);
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success'
    });
    
    const [formData, setFormData] = useState<FormData>({
        id: null,
        planName: '',
        hostingRate: '',
        hydroSetupFee: '',
        airSetupFee: '',
        depositDays: '',
        description: ''
    });

    // Fetch all hosting plans
    const fetchHostingPlans = async (): Promise<void> => {
        setLoading(true);
        try {
            const response = await axios.get<HostingPlan[]>(API_PATH + '/hostingsetup/list', getAxiosConfig());
            setHostingPlans(response.data);
        } catch (error) {
            console.error('Error fetching hosting plans:', error);
            setSnackbar({
                open: true,
                message: 'Failed to fetch hosting plans',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Save or update hosting plan
    const saveHostingPlan = async (planData: HostingPlan): Promise<void> => {
        setLoading(true);
        try {
            const response = await axios.post<HostingPlan>(API_PATH + '/hostingsetup/save', planData, getAxiosConfig());
            
            if (planData.id) {
                // Update existing plan
                setHostingPlans(prevPlans => 
                    prevPlans.map(plan => 
                        plan.id === response.data.id ? response.data : plan
                    )
                );
                setSnackbar({
                    open: true,
                    message: 'Hosting plan updated successfully',
                    severity: 'success'
                });
            } else {
                // Add new plan
                setHostingPlans(prevPlans => [...prevPlans, response.data]);
                setSnackbar({
                    open: true,
                    message: 'Hosting plan added successfully',
                    severity: 'success'
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error('Error saving hosting plan:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save hosting plan',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchHostingPlans();
    }, []);

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        
        // Convert string values to numbers for numeric fields
        const processedData: HostingPlan = {
            id: formData.id || null,
            planName: formData.planName,
            hostingRate: parseFloat(formData.hostingRate) || 0,
            hydroSetupFee: parseFloat(formData.hydroSetupFee) || 0,
            airSetupFee: parseFloat(formData.airSetupFee) || 0,
            depositDays: parseInt(formData.depositDays) || 0,
            description: formData.description
        };

        saveHostingPlan(processedData);
    };

    // Open dialog for adding new plan
    const handleAddNew = (): void => {
        setEditingPlan(null);
        setFormData({
            id: null,
            planName: '',
            hostingRate: '',
            hydroSetupFee: '',
            airSetupFee: '',
            depositDays: '',
            description: ''
        });
        setDialogOpen(true);
    };

    // Open dialog for editing plan
    const handleEdit = (plan: HostingPlan): void => {
        setEditingPlan(plan);
        setFormData({
            id: plan.id,
            planName: plan.planName,
            hostingRate: plan.hostingRate.toString(),
            hydroSetupFee: plan.hydroSetupFee.toString(),
            airSetupFee: plan.airSetupFee.toString(),
            depositDays: plan.depositDays.toString(),
            description: plan.description
        });
        setDialogOpen(true);
    };

    // Close dialog
    const handleCloseDialog = (): void => {
        setDialogOpen(false);
        setEditingPlan(null);
    };

    // Close snackbar
    const handleCloseSnackbar = (): void => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Format currency
    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 3
        }).format(value);
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header Section */}
            <Box
                display="flex"
                position="sticky"
                top="70px"
                zIndex={100}
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                gap={1.5}
                sx={{
                    mb: 2,
                    backgroundColor: '#d1e8ff',
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    borderRadius: 1,
                }}
                >
                {/* Left: Title */}
                <Typography
                    variant="h5"
                    sx={{
                    fontFamily: 'monospace',
                    color: '#1565c0',
                    textAlign: { xs: 'center', sm: 'left' },
                    whiteSpace: 'nowrap',
                    }}
                >
                    Hosting Setup Management
                </Typography>

                {/* Right: Date range + Search */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={{ xs: 'center', sm: 'flex-end' }}
                    gap={1.5}
                    flexWrap="wrap"
                >

                    <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddNew}
                            disabled={loading}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Add New Plan
                        </Button>
                </Box>
            </Box>

            {/* Table Section */}
            <Paper 
                elevation={0} 
                sx={{ 
                    width: '100%', 
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Plan Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Hosting Rate</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Hydro Fee</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Air Fee</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Deposit Days</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && hostingPlans.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                        <CircularProgress />
                                        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                            Loading hosting plans...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : hostingPlans.length > 0 ? (
                                hostingPlans.map((plan) => (
                                    <TableRow 
                                        key={plan.id}
                                        hover
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>{plan.id}</TableCell>
                                        <TableCell>
                                            <Typography fontWeight="500">
                                                {plan.planName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatCurrency(plan.hostingRate)}</TableCell>
                                        <TableCell>{formatCurrency(plan.hydroSetupFee)}</TableCell>
                                        <TableCell>{formatCurrency(plan.airSetupFee)}</TableCell>
                                        <TableCell>{plan.depositDays} days</TableCell>
                                        <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography variant="body2" noWrap>
                                                {plan.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(plan)}
                                                disabled={loading}
                                                sx={{
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                                    }
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No hosting plans found
                                        </Typography>
                                        <Button
                                            variant="text"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddNew}
                                            sx={{ mt: 2 }}
                                        >
                                            Add your first plan
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 2
                }}>
                    <Typography variant="h6" fontWeight="600">
                        {editingPlan ? 'Edit Hosting Plan' : 'Add New Hosting Plan'}
                    </Typography>
                    <IconButton onClick={handleCloseDialog} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{ pt: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Plan Name"
                                    name="planName"
                                    value={formData.planName}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Hosting Rate"
                                    name="hostingRate"
                                    type="number"
                                    value={formData.hostingRate}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ step: '0.001', min: 0 }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Hydro Setup Fee"
                                    name="hydroSetupFee"
                                    type="number"
                                    value={formData.hydroSetupFee}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ step: '0.01', min: 0 }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Air Setup Fee"
                                    name="airSetupFee"
                                    type="number"
                                    value={formData.airSetupFee}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ step: '0.01', min: 0 }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Deposit Days"
                                    name="depositDays"
                                    type="number"
                                    value={formData.depositDays}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Button 
                            onClick={handleCloseDialog}
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{ 
                                textTransform: 'none',
                                position: 'relative'
                            }}
                        >
                            {loading && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px'
                                    }}
                                />
                            )}
                            {editingPlan ? 'Update Plan' : 'Save Plan'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}